// "Certificates expiring soon" dashboard widget. Lists certs already expired or
// expiring within `withinDays`, soonest first, with a RAG badge. Used on both
// the client portal dashboard (client-scoped) and the internal dashboard
// (portfolio-wide, shows the client name).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, ArrowRight, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_STYLE = {
    expired: { bg: "#FBE8E5", fg: "#B33A2E", label: "Expired" },
    critical: { bg: "#FBE8E5", fg: "#B33A2E", label: "Expiring soon" },
    warning: { bg: "#FCF1DC", fg: "#C68A1C", label: "Renew soon" },
};

function badgeText(c) {
    const d = c.days_to_expiry;
    if (c.expiry_status === "expired" && d != null) return `Expired ${Math.abs(d)}d ago`;
    if (d != null) return `${d}d left`;
    return (STATUS_STYLE[c.expiry_status] || {}).label || "";
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
}

export default function CertExpiryWidget({ clientId = null, brand = "var(--brand-accent, #C75A1B)", showClientName = false, headerless = false }) {
    const [data, setData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const params = { within_days: 90 };
        if (clientId) params.client_id = clientId;
        api.get("/certificates/expiring", { params })
            .then((r) => setData(r.data))
            .catch(() => setData({ items: [], counts: {} }));
    }, [clientId]);

    const items = data?.items || [];

    const list = (
        <ul>
            {items.slice(0, 6).map((c) => {
                const s = STATUS_STYLE[c.expiry_status] || STATUS_STYLE.warning;
                return (
                    <li key={c.id} data-testid={`cert-expiry-item-${c.id}`}
                        className="flex items-center justify-between px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer"
                        style={{ borderColor: "#F0EDE8" }} onClick={() => navigate("/app/certificates")}>
                        <div className="flex items-center gap-2.5 min-w-0">
                            {c.expiry_status !== "warning" && <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-[#B33A2E]" />}
                            <div className="min-w-0">
                                <div className="text-[12.5px] font-medium text-[#1F1F1F] truncate">{c.title}</div>
                                <div className="text-[10.5px]" style={{ color: "#9A968F" }}>
                                    {showClientName && c.client_name ? `${c.client_name} · ` : ""}
                                    {c.issuing_body ? `${c.issuing_body} · ` : ""}exp {fmtDate(c.expiry_date)}
                                </div>
                            </div>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0"
                            style={{ background: s.bg, color: s.fg }}>
                            {badgeText(c)}
                        </span>
                    </li>
                );
            })}
            {items.length === 0 && (
                <li className="px-5 py-6 italic text-center text-[#9A968F] text-[12px]">
                    {data ? "No certificates expiring in the next 90 days." : "Loading…"}
                </li>
            )}
        </ul>
    );

    if (headerless) return <div data-testid="cert-expiry-widget">{list}</div>;

    return (
        <div data-testid="cert-expiry-widget" className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#F0EDE8" }}>
                <div className="flex items-center gap-2">
                    <BadgeCheck className="w-4 h-4" style={{ color: brand }} />
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Certificates expiring soon</h3>
                    {items.length > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#FBE8E5", color: "#B33A2E" }}>
                            {items.length}
                        </span>
                    )}
                </div>
                <button onClick={() => navigate("/app/certificates")}
                    className="text-[11.5px] font-semibold flex items-center gap-1 hover:underline" style={{ color: brand }}>
                    View all <ArrowRight className="w-3 h-3" />
                </button>
            </div>
            {list}
        </div>
    );
}
